// -----------------------------------------------------------------------------
// src/pages/Dashboard.js - WITH PAGE PAGINATION & DATE FILTERS
// -----------------------------------------------------------------------------
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchReportsThunk } from '../features/reports/reportThunks';
import { fetchMonthlyStatsThunk } from '../features/reports/reportThunks';
import { fetchEmployeesThunk } from '../features/employees/employeeThunks';
import { fetchStoresThunk } from '../features/stores/storeThunks';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, 
  CheckCircle, 
  Clock, 
  AlertTriangle, 
  XCircle, 
  TrendingUp, 
  TrendingDown, 
  ChevronRight,
  Star,
  Filter,
  X,
  AlertCircle,
  Calendar,
  ChevronLeft,
  ChevronDown
} from 'lucide-react';
import '../CSS/Dashboard.css';
import IncentiveInfo from './IncentiveInfo';
import StoreAttentionTable from './StoreAttentionTable';
import ReportCountDashboard from './ReportCountDashboard';
import TransactionAlertsTable from './TransactionAlertsTable';
import TriggerAlertsTable from './TriggerAlertsTable';
import ReportStatistics from './ReportStatistics';
import TopPerformers from './TopPerformers';

// Helper function to format dates
const formatDate = (date) => {
  return date.toISOString().split('T')[0];
};

// Helper function to get date ranges
// Helper function to get date ranges - FIXED VERSION
const getDateRange = (period) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  switch (period) {
    case 'all':
      return { startDate: null, endDate: null, label: 'All Time' };
      
    case 'last_month':
      // Get the previous month properly
      const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0); // Last day of previous month
      return { 
        startDate: formatDate(lastMonthStart), 
        endDate: formatDate(lastMonthEnd), 
        label: 'Last Month' 
      };
      
    case 'last_3_months':
      // Get exactly 3 months ago to today
      const threeMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
      return { 
        startDate: formatDate(threeMonthsAgo), 
        endDate: formatDate(today), 
        label: 'Last 3 Months' 
      };
      
    case 'current_month':
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      return { 
        startDate: formatDate(firstDayOfMonth), 
        endDate: formatDate(today), 
        label: 'Current Month' 
      };
      
    default:
      return { startDate: null, endDate: null, label: 'All Time' };
  }
};

// Helper function to format current month range for display
const getCurrentMonthRange = () => {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const monthName = now.toLocaleString('en-US', { month: 'short' });
  const firstDayFormatted = `${monthName} 1`;
  const lastDayFormatted = `${monthName} ${lastDay.getDate()}, ${now.getFullYear()}`;
  return `${firstDayFormatted} - ${lastDayFormatted}`;
};

// All the helper functions remain the same...
const formatIncidentTime = (timeString) => {
  if (!timeString) return 'N/A';
  
  try {
    const [hours, minutes] = timeString.split(':');
    const hour24 = parseInt(hours, 10);
    const minute = parseInt(minutes, 10);
    
    const period = hour24 >= 12 ? 'PM' : 'AM';
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
    
    const formattedMinutes = minute.toString().padStart(2, '0');
    
    return `${hour12}:${formattedMinutes} ${period}`;
  } catch (error) {
    console.error('Error formatting incident time:', error);
    return 'N/A';
  }
};

const getEmployeeFullName = (employee) => {
  if (!employee) return 'Unknown Employee';
  
  if (employee.name) return employee.name;
  
  if (employee.user) {
    const firstName = employee.user.first_name || '';
    const lastName = employee.user.last_name || '';
    return `${firstName} ${lastName}`.trim() || 'Unknown Employee';
  }
  
  return 'Unknown Employee';
};

const getStoreImageUrl = (store) => {
  if (store?.image) {
    return store.image;
  }
  return `/api/placeholder/40/40?text=${store?.store_name?.charAt(0) || 'S'}`;
};

const getEmployeeImageUrl = (employee) => {
  if (employee?.image) {
    return employee.image;
  }
  const name = getEmployeeFullName(employee);
  return `/api/placeholder/40/40?text=${name.charAt(0) || 'U'}`;
};

// Timeframe Selector Component
const TimeframeSelector = ({ selectedTimeframe, onTimeframeChange, customDateRange, onCustomDateChange }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isCustomDateOpen, setIsCustomDateOpen] = useState(false);

  const timeframeOptions = [
    { value: 'current_month', label: 'Current Month' },
    { value: 'last_month', label: 'Last Month' },
    { value: 'last_3_months', label: 'Last 3 Months' },
    { value: 'all', label: 'All Time' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const getCurrentLabel = () => {
    if (selectedTimeframe === 'custom' && customDateRange.startDate && customDateRange.endDate) {
      return `${customDateRange.startDate} to ${customDateRange.endDate}`;
    }
    const option = timeframeOptions.find(opt => opt.value === selectedTimeframe);
    return option ? option.label : 'Current Month';
  };

  return (
    <div className="timeframe-selector">
      <span className="timeframe-label">Timeframe</span>
      <div className="timeframe-dropdown">
        <button 
          className="timeframe-button"
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        >
          <Calendar size={16} />
          <span>{getCurrentLabel()}</span>
          <ChevronDown size={16} />
        </button>
        
        {isDropdownOpen && (
          <div className="timeframe-dropdown-menu">
            {timeframeOptions.map((option) => (
              <button
                key={option.value}
                className={`timeframe-option ${selectedTimeframe === option.value ? 'active' : ''}`}
                onClick={() => {
                  if (option.value === 'custom') {
                    setIsCustomDateOpen(true);
                  } else {
                    onTimeframeChange(option.value);
                    setIsDropdownOpen(false);
                  }
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Custom Date Range Modal */}
      {isCustomDateOpen && (
        <div className="date-range-modal">
          <div className="date-range-content">
            <h3>Select Date Range</h3>
            <div className="date-inputs">
              <div className="date-input-group">
                <label>Start Date</label>
                <input
                  type="date"
                  value={customDateRange.startDate || ''}
                  onChange={(e) => onCustomDateChange('startDate', e.target.value)}
                />
              </div>
              <div className="date-input-group">
                <label>End Date</label>
                <input
                  type="date"
                  value={customDateRange.endDate || ''}
                  onChange={(e) => onCustomDateChange('endDate', e.target.value)}
                />
              </div>
            </div>
            <div className="date-range-actions">
              <button 
                className="cancel-btn"
                onClick={() => {
                  setIsCustomDateOpen(false);
                  setIsDropdownOpen(false);
                }}
              >
                Cancel
              </button>
              <button 
                className="apply-btn"
                onClick={() => {
                  onTimeframeChange('custom');
                  setIsCustomDateOpen(false);
                  setIsDropdownOpen(false);
                }}
                disabled={!customDateRange.startDate || !customDateRange.endDate}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Pagination Component
const Pagination = ({ currentPage, totalPages, totalItems, itemsPerPage, onPageChange }) => {
  const getVisiblePages = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];
    let l;

    for (let i = Math.max(2, currentPage - delta);
         i <= Math.min(totalPages - 1, currentPage + delta);
         i++) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  const startItem = ((currentPage - 1) * itemsPerPage) + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className="pagination-container">
      <div className="pagination-info">
        Showing {startItem}-{endItem} of {totalItems} reports
      </div>
      <div className="pagination-controls">
        <button
          className="pagination-btn"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <ChevronLeft size={16} />
          Previous
        </button>
        
        <div className="pagination-pages">
          {getVisiblePages().map((page, index) => (
            <button
              key={index}
              className={`pagination-page ${page === currentPage ? 'active' : ''} ${page === '...' ? 'dots' : ''}`}
              onClick={() => typeof page === 'number' && onPageChange(page)}
              disabled={page === '...'}
            >
              {page}
            </button>
          ))}
        </div>
        
        <button
          className="pagination-btn"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};

// StatCard and other components remain the same...
const StatCard = ({ 
  title, 
  value, 
  subtitle, 
  dark, 
  icon: IconComponent, 
  loading = false,
  onClick,
  isActive = false,
  statusFilter
}) => {
  if (loading) {
    return (
      <div className={`stat-card ${dark ? 'dark' : 'light'} skeleton-card`}>
        <div className="card-content">
          <div className="card-header">
            <div className="skeleton-text skeleton-title"></div>
            <div className="skeleton-circle skeleton-icon"></div>
          </div>
          
          <div className="skeleton-text skeleton-value"></div>
          
          <div className="card-footer">
            <div className="user">
              <div className="skeleton-circle"></div>
              <div className="skeleton-text skeleton-subtitle"></div>
            </div>
            <div className="skeleton-circle skeleton-button-circle"></div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div 
      className={`stat-card ${dark ? 'dark' : 'light'} ${isActive ? 'active-filter' : ''} ${onClick ? 'clickable' : ''}`}
      onClick={onClick}
    >
      <div className="card-content">
        <div className="card-header">
          <h3 className="title">{title}</h3>
          <Star className="star-icon" size={20} />
        </div>
        
        <div className="value">
          {value >= 1000 ? `${value.toLocaleString()}` : value}
        </div>
        
        <div className="card-footer">
          <div className="user">
            <div className="avatar">
              <IconComponent size={16} />
            </div>
            <span className="name">{subtitle}</span>
          </div>
          <button className="arrow-button">
            <ChevronRight size={20} />
          </button>
        </div>
      </div>
    </div>
  );
};

// ActiveFilters and TableRowSkeleton components remain the same...
const ActiveFilters = ({ filters, onClearFilter, onClearAll }) => {
  const activeFilters = [];
  
  if (filters.severity) {
    activeFilters.push({ key: 'severity', label: `Severity: ${filters.severity}`, value: filters.severity });
  }
  if (filters.store) {
    activeFilters.push({ key: 'store', label: `Store: ${filters.storeName || filters.store}`, value: filters.store });
  }
  if (filters.status) {
    activeFilters.push({ key: 'status', label: `Status: ${filters.status.replace('_', ' ')}`, value: filters.status });
  }
  if (filters.employee) {
    activeFilters.push({ key: 'employee', label: `Employee: ${filters.employeeName || filters.employee}`, value: filters.employee });
  }

  if (activeFilters.length === 0) return null;

  return (
    <div className="active-filters">
      <span className="active-filters-label">Active filters:</span>
      <div className="filter-tags">
        {activeFilters.map((filter) => (
          <span key={filter.key} className="filter-tag">
            {filter.label}
            <button
              className="filter-tag-remove"
              onClick={() => onClearFilter(filter.key)}
              aria-label={`Remove ${filter.label} filter`}
            >
              <X size={12} />
            </button>
          </span>
        ))}
        <button className="clear-all-filters" onClick={onClearAll}>
          Clear all
        </button>
      </div>
    </div>
  );
};

const TableRowSkeleton = () => {
  return (
    <tr className="skeleton-row">
      <td>
        <div className="store-info">
          <div className="skeleton-circle"></div>
          <div className="skeleton-text"></div>
        </div>
      </td>
      <td>
        <div className="date-info">
          <div className="skeleton-text"></div>
          <div className="skeleton-text skeleton-text-small"></div>
        </div>
      </td>
      <td>
        <div className="creator-info">
          <div className="skeleton-circle"></div>
          <div className="skeleton-text"></div>
        </div>
      </td>
      <td><div className="skeleton-text"></div></td>
      <td><div className="skeleton-badge"></div></td>
      <td><div className="skeleton-badge"></div></td>
      <td><div className="skeleton-badge"></div></td>
      <td>
        <div className="reason-cell">
          <div className="skeleton-text"></div>
          <div className="skeleton-text skeleton-text-small"></div>
        </div>
      </td>
      <td>
        <div className="action-buttons">
          <div className="skeleton-button"></div>
          <div className="skeleton-button"></div>
        </div>
      </td>
    </tr>
  );
};

function Dashboard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Timeframe state
  const [selectedTimeframe, setSelectedTimeframe] = useState('current_month');
  const [customDateRange, setCustomDateRange] = useState({
    startDate: '',
    endDate: ''
  });

  // Filter state
  const [filters, setFilters] = useState({
    severity: '',
    store: '',
    storeName: '',
    status: '',
    employee: '',
    employeeName: '',
    search: ''
  });

  const searchTimeoutRef = useRef(null);

  // Pull data from Redux store
  const { employees = [] } = useSelector((state) => state.employees || {});
  const { stores = [] } = useSelector((state) => state.stores || {});

  const {
    reports: reportList = [],
    loading = false,
    error = null,
    count: totalReports = 0,
    monthlyStats = {},
  } = useSelector((state) => state.reports || {});

  const {
    month = '',
    year = new Date().getFullYear(),
    totalReports: statsTotal = 0,
    statusBreakdown = {},
    loading: statsLoading = false,
  } = monthlyStats;

  const submittedReviewCount = statusBreakdown?.SUBMITTED_REVIEW || 0;
  const submittedApprovalCount = statusBreakdown?.SUBMITTED_APPROVAL || 0;
  const approvedCount = statusBreakdown?.APPROVED || 0;
  const declinedCount = statusBreakdown?.DECLINED || 0;
  const discrepancyCount = statusBreakdown?.DISCREPANCY || 0;

  // Calculate total pages
  const totalPages = Math.ceil(totalReports / itemsPerPage);

  // Get current date range based on timeframe
  const getCurrentDateRange = useCallback(() => {
    if (selectedTimeframe === 'custom') {
      return {
        startDate: customDateRange.startDate,
        endDate: customDateRange.endDate
      };
    }
    return getDateRange(selectedTimeframe);
  }, [selectedTimeframe, customDateRange]);

  // Fetch reports function
  const fetchReports = useCallback((page = 1, filterOverrides = null) => {
    const filtersToUse = filterOverrides || filters;
    const dateRange = getCurrentDateRange();
    
    const params = {
      offset: (page - 1) * itemsPerPage,
      limit: itemsPerPage,
    };

    if (filtersToUse.store) params.store_id = filtersToUse.store;
    if (filtersToUse.status) params.status = filtersToUse.status;
    if (filtersToUse.employee) params.employee_id = filtersToUse.employee;
    if (filtersToUse.severity) params.severity = filtersToUse.severity;
    if (filtersToUse.search) params.search = filtersToUse.search;

    // Add date range parameters
    if (dateRange.startDate) params.start_date = dateRange.startDate;
    if (dateRange.endDate) params.end_date = dateRange.endDate;

    console.log('Fetching reports with params:', params, 'page:', page);
    dispatch(fetchReportsThunk(params));
  }, [dispatch, filters, getCurrentDateRange, itemsPerPage]);

  // Fetch stats function
  const fetchStats = useCallback((filterOverrides = null) => {
    const filtersToUse = filterOverrides || filters;
    const dateRange = getCurrentDateRange();
    
    const params = {};
    if (filtersToUse.store) params.store_id = filtersToUse.store;
    if (dateRange.startDate) params.start_date = dateRange.startDate;
    if (dateRange.endDate) params.end_date = dateRange.endDate;
    
    console.log('Fetching stats with params:', params);
    dispatch(fetchMonthlyStatsThunk(params));
  }, [dispatch, filters, getCurrentDateRange]);

  // Handle page change
  const handlePageChange = useCallback((page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      fetchReports(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [totalPages, fetchReports]);

  // Handle timeframe change
  const handleTimeframeChange = useCallback((timeframe) => {
    setSelectedTimeframe(timeframe);
    setCurrentPage(1); // Reset to first page when changing timeframe
  }, []);

  // Handle custom date change
  const handleCustomDateChange = useCallback((field, value) => {
    setCustomDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  // Effect for timeframe changes
  useEffect(() => {
    fetchReports(1);
    fetchStats();
  }, [selectedTimeframe, customDateRange.startDate, customDateRange.endDate]);

  // Search debouncing effect
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (filters.search !== '') {
      searchTimeoutRef.current = setTimeout(() => {
        console.log('Debounced search triggered');
        setCurrentPage(1);
        fetchReports(1);
        fetchStats();
      }, 500);
    } else if (searchTimeoutRef.current) {
      setCurrentPage(1);
      fetchReports(1);
      fetchStats();
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [filters.search]);

  // Non-search filters effect
  useEffect(() => {
    if (filters.search !== '') {
      return;
    }

    console.log('Non-search filters changed, fetching...');
    setCurrentPage(1);
    fetchReports(1);
    fetchStats();
  }, [filters.severity, filters.store, filters.status, filters.employee]);

  // Initial data loading
  useEffect(() => {
    if (employees.length === 0) dispatch(fetchEmployeesThunk());
    if (stores.length === 0) dispatch(fetchStoresThunk());
  }, [dispatch, employees.length, stores.length]);

  // Handle filter changes
  const handleFilterChange = useCallback((filterType, value, displayName = '') => {
    console.log(`Filter change: ${filterType} = ${value}`);
    
    setFilters(prev => ({
      ...prev,
      [filterType]: value,
      ...(filterType === 'store' && { storeName: displayName }),
      ...(filterType === 'employee' && { employeeName: displayName })
    }));
  }, []);

  // Handle stat card clicks for status filtering
  const handleStatCardClick = useCallback((status) => {
    if (filters.status === status) {
      handleFilterChange('status', '');
    } else {
      handleFilterChange('status', status);
    }
  }, [filters.status, handleFilterChange]);

  // Clear individual filter
  const handleClearFilter = useCallback((filterKey) => {
    console.log('Clearing filter:', filterKey);
    
    setFilters(prev => {
      const newFilters = { ...prev };
      newFilters[filterKey] = '';
      
      if (filterKey === 'store') {
        newFilters.storeName = '';
      } else if (filterKey === 'employee') {
        newFilters.employeeName = '';
      }
      
      return newFilters;
    });
  }, []);

  // Clear all filters
  const handleClearAllFilters = useCallback(() => {
    console.log('Clearing all filters');
    
    setFilters({
      severity: '',
      store: '',
      storeName: '',
      status: '',
      employee: '',
      employeeName: '',
      search: ''
    });
  }, []);

  const handleRowClick = useCallback((reportId) => {
    // Open report in new tab
    window.open(`/reports/${reportId}/edit`, '_blank');
  }, []);

  const handleViewReport = useCallback((reportId) => {
    // Open report view in new tab
    window.open(`/reports/${reportId}`, '_blank');
  }, []);

  const handleEditReport = useCallback((reportId) => {
    // Open report edit in new tab
    window.open(`/reports/${reportId}/edit`, '_blank');
  }, []);

  const skeletonRows = Array(5).fill().map((_, index) => (
    <TableRowSkeleton key={`skeleton-${index}`} />
  ));
  const teamMembers = employees.slice(0, 3);
  const displayedReports = reportList;

  return (
    <div className="dashboard-container">
      {/* Header section */}
      <div style={{ margin: '1rem 0' }}>
        <button className="create-report-btn" onClick={() => navigate('/reports/new')}>+ Create New Report</button>
      </div>
      
      <div className="revenue-dashboard__header">
        <div className="revenue-dashboard__title-wrapper">
          <h1 className="revenue-dashboard__title">Dashboard</h1>
          <div className="revenue-dashboard__avatars">
            {teamMembers.length > 0 ? (
              teamMembers.map((employee, index) => (
                <div 
                  key={employee.id || index} 
                  className={`revenue-dashboard__avatar revenue-dashboard__avatar--${['blue', 'green', 'purple'][index % 3]}`}
                >
                  <img 
                    src={getEmployeeImageUrl(employee)} 
                    alt={getEmployeeFullName(employee)} 
                    className="avatar-img" 
                  />
                </div>
              ))
            ) : (
              <>
                <div className="revenue-dashboard__avatar revenue-dashboard__avatar--blue">
                  <img src="/api/placeholder/28/28?text=JD" alt="Team member" className="avatar-img" />
                </div>
                <div className="revenue-dashboard__avatar revenue-dashboard__avatar--green">
                  <img src="/api/placeholder/28/28?text=MS" alt="Team member" className="avatar-img" />
                </div>
                <div className="revenue-dashboard__avatar revenue-dashboard__avatar--purple">
                  <img src="/api/placeholder/28/28?text=AL" alt="Team member" className="avatar-img" />
                </div>
              </>
            )}
            <div className="revenue-dashboard__avatar revenue-dashboard__avatar--add">
              <span>+</span>
            </div>
          </div>
        </div>
        
        {/* NEW: Timeframe Selector */}
        <TimeframeSelector
          selectedTimeframe={selectedTimeframe}
          onTimeframeChange={handleTimeframeChange}
          customDateRange={customDateRange}
          onCustomDateChange={handleCustomDateChange}
        />
      </div>

      {/* Statistics cards section */}
      <div className="statistics-box">
        <div className="revenue-card">
          {statsLoading ? (
            <div className="skeleton-revenue-card">
              <div className="skeleton-text skeleton-label"></div>
              <div className="skeleton-text skeleton-amount"></div>
              <div className="skeleton-badge skeleton-change"></div>
              <div className="skeleton-text skeleton-comparison"></div>
            </div>
          ) : (
            <>
              <div className="revenue-card__label">Total Reports</div>
              <div className="revenue-card__amount-wrapper">
                <span className="revenue-card__amount">{statsTotal.toLocaleString()}</span>
                <span className="revenue-card__change">
                  <TrendingUp size={14} />
                  15%
                </span>
              </div>
              <div className="revenue-card__comparison">vs previous period</div>
            </>
          )}
        </div>

        <div className="metrics-grid">
          <StatCard 
            title="Approved"
            value={approvedCount}
            subtitle={getCurrentDateRange().label}
            dark={false}
            icon={CheckCircle}
            loading={statsLoading}
            onClick={() => handleStatCardClick('APPROVED')}
            isActive={filters.status === 'APPROVED'}
            statusFilter="APPROVED"
          />
          
          <StatCard 
            title="Review"
            value={submittedReviewCount}
            subtitle="Pending review"
            dark={true}
            icon={Clock}
            loading={statsLoading}
            onClick={() => handleStatCardClick('SUBMITTED_REVIEW')}
            isActive={filters.status === 'SUBMITTED_REVIEW'}
            statusFilter="SUBMITTED_REVIEW"
          />
          
          <StatCard 
            title="Approval"
            value={submittedApprovalCount}
            subtitle="In progress"
            dark={false}
            icon={FileText}
            loading={statsLoading}
            onClick={() => handleStatCardClick('SUBMITTED_APPROVAL')}
            isActive={filters.status === 'SUBMITTED_APPROVAL'}
            statusFilter="SUBMITTED_APPROVAL"
          />
          
          <StatCard 
            title="Declined"
            value={declinedCount}
            subtitle="Rejected"
            dark={false}
            icon={XCircle}
            loading={statsLoading}
            onClick={() => handleStatCardClick('DECLINED')}
            isActive={filters.status === 'DECLINED'}
            statusFilter="DECLINED"
          />

          <StatCard 
            title="Discrepancy"
            value={discrepancyCount}
            subtitle="Issues found"
            dark={true}
            icon={AlertCircle}
            loading={statsLoading}
            onClick={() => handleStatCardClick('DISCREPANCY')}
            isActive={filters.status === 'DISCREPANCY'}
            statusFilter="DISCREPANCY"
          />
        </div>
      </div>

      {/* Search and filter section - same as before */}
      <div className="search-filter-container">
        <div className="search-wrapper">
          <div className="modern-search-box">
            <svg className="search-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input 
              type="text" 
              placeholder="Search reports by store or employee..." 
              value={filters.search}
              onChange={(e) => {
                console.log('Search input changed:', e.target.value);
                handleFilterChange('search', e.target.value);
              }}
            />
          </div>
          
          <div className="filter-controls">
            <div className="severity-filter">
              <select 
                className="modern-select"
                value={filters.severity}
                onChange={(e) => {
                  console.log('Severity filter changed:', e.target.value);
                  handleFilterChange('severity', e.target.value);
                }}
              >
                <option value="">All Severities</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>

            <div className="store-filter">
              <select 
                className="modern-select"
                value={filters.store}
                onChange={(e) => {
                  const selectedStore = stores.find(store => store.id.toString() === e.target.value);
                  console.log('Store filter changed:', e.target.value, selectedStore?.store_name);
                  handleFilterChange('store', e.target.value, selectedStore?.store_name || '');
                }}
              >
                <option value="">All Stores</option>
                {stores.map(store => (
                  <option key={store.id} value={store.id}>
                    {store.store_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="employee-filter">
              <select 
                className="modern-select"
                value={filters.employee}
                onChange={(e) => {
                  const selectedEmployee = employees.find(emp => emp.id.toString() === e.target.value);
                  const employeeName = getEmployeeFullName(selectedEmployee);
                  console.log('Employee filter changed:', e.target.value, employeeName);
                  handleFilterChange('employee', e.target.value, employeeName);
                }}
              >
                <option value="">All Employees</option>
                {employees.map(employee => (
                  <option key={employee.id} value={employee.id}>
                    {getEmployeeFullName(employee)}
                  </option>
                ))}
              </select>
            </div>

            <button className="filter-button">
              <Filter size={16} />
              <span>More</span>
              <span className="filter-count">
                {Object.values(filters).filter(v => v !== '').length}
              </span>
            </button>
          </div>
        </div>
      </div>

      <ActiveFilters 
        filters={filters}
        onClearFilter={handleClearFilter}
        onClearAll={handleClearAllFilters}
      />

      {/* Table section */}
      <div className="table-container">
        <div className="table-wrapper">
          {error && <p className="error-message">
            {typeof error === 'object' ? error.message || JSON.stringify(error) : error}
          </p>}
          <div className="table-header">
            <h2>
              Reports 
              {(filters.severity || filters.search || filters.store || filters.status || filters.employee) && (
                <span className="filtered-count">
                  (filtered results)
                </span>
              )}
            </h2>
            <div className="table-actions">
              <button className="table-action-btn">Export</button>
              <button 
                className="table-action-btn"
                onClick={handleClearAllFilters}
                disabled={Object.values(filters).every(v => v === '')}
              >
                Clear Filters
              </button>
            </div>
          </div>
          
          <div className="table-scroll-container">
            <div className="scroll-indicator-left"></div>
            <div className="scroll-indicator-right"></div>
            
            <table className="modern-table">
              <thead>
                <tr>
                  <th className="col-business">Business</th>
                  <th>Report Date</th>
                  <th className="col-creator">Created By</th>
                  <th>Incident Type</th>
                  <th>Severity</th>
                  <th>Status</th>
                  <th>Involves</th>
                  <th>Reason</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && reportList.length === 0 ? (
                  skeletonRows
                ) : (
                  displayedReports.map((report) => (
                    <tr key={report.id} onClick={() => handleRowClick(report.id)}>
                      <td className="col-business">
                        <div className="store-info">
                          <div className="store-image-container">
                            <img 
                              src={getStoreImageUrl(report.store)}
                              alt={report.store?.store_name || "Store"}
                              className="store-image"
                            />
                          </div>
                          <span className="store-name">{report.store?.store_name}</span>
                        </div>
                      </td>
                      <td>
                        <div className="date-info">
                          <span className="date-primary">{report.incident_date}</span>
                          <span className="date-secondary">{formatIncidentTime(report.incident_time)}</span>
                        </div>
                      </td>
                      <td className="col-creator">
                        <div className="creator-info">
                          <div className="creator-image-container">
                            <img 
                              src={getEmployeeImageUrl(report.employee)}
                              alt={getEmployeeFullName(report.employee)}
                              className="creator-image"
                            />
                          </div>
                          <span className="creator-name">{getEmployeeFullName(report.employee)}</span>
                        </div>
                      </td>
                      <td>
                        <span className="incident-type">
                          {report.incident_type}
                        </span>
                      </td>
                      <td>
                        <span className={`severity-badge ${report.severity?.toLowerCase() || 'unknown'}`}>
                          {report.severity || 'Low'}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${report.status?.toLowerCase()}`}>
                          {report.status}
                        </span>
                      </td>
                      <td>
                        <span className="involves-badge">
                          {report.incident_type === 'cashierSuspicious' ? 'Cashier' : 'Customer'}
                        </span>
                      </td>
                      <td>
                        <div className="reason-cell">
                          <span className="reason-text">
                            {report.call_status === 'CALLED'
                              ? `${report.reason_called}`
                              : `${report.reason_not_called}`}
                          </span>
                          <span className="call-status">
                            {report.call_status === 'CALLED' ? 'Called' : 'Not Called'}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button 
                            className="action-btn edit"
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent row click
                              handleEditReport(report.id);
                            }}
                          >
                            <span>Edit</span>
                          </button>
                          <button 
                            className="action-btn view"
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent row click
                              handleViewReport(report.id);
                            }}
                          >
                            <span>View</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* NEW: Pagination instead of Load More */}
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalReports}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      </div>
      
      {/* Additional components remain the same */}
      <TransactionAlertsTable />

      <div className="store-attention-report-count">
        <div className='incentive-top-box'>
          <IncentiveInfo />
          <TopPerformers />
        </div>
        <TriggerAlertsTable />
      </div>
      
      <ReportStatistics />
    </div>
  );
}

export default Dashboard;